import { BillingDefermentStateEnum } from '.spec/self-care/tsTypes';

import type { MyContract } from '@skippay/core/@types/myContract';
import { toFloat } from '@skippay/core/utils/amountUtils';
import type { Paginated } from '@skippay/core/utils/queryUtils';

import type { Billing } from '@types/billing';
import { toIsoDate } from 'jarabot/utils';
import { isBillingPayable } from 'utils/billingUtils';

import JarabotBillingQuery from '../../queries/JarabotBillingQuery.graphql';
import JarabotContractQuery from '../../queries/JarabotContractQuery.graphql';
import { BillingInfo, LimitInfo, RevolvingInfo } from '../../types';
import type { DomainSource } from '../types';

interface ContractData {
	myContract?: {
		availableAmount?: MyContract['availableAmount'];
		totalLimit?: MyContract['totalLimit'];
		availableAmountState?: MyContract['availableAmountState'];
		availableAnnualAmount?: MyContract['availableAnnualAmount'];
		totalAnnualLimit?: MyContract['totalAnnualLimit'];
		limitAmendmentEntryState?: MyContract['limitAmendmentEntryState'];
		preAmendmentLimitIncreaseOffer?: MyContract['preAmendmentLimitIncreaseOffer'];
		revolvingLoanContractAdditionalInfo?: MyContract['revolvingLoanContractAdditionalInfo'];
	} | null;
}

export const contractSource: DomainSource<ContractData> = {
	domains: ['limit', 'revolving'],
	query: JarabotContractQuery,
	toModel: (data) => {
		const contract = data.myContract;
		if (!contract) return {};
		const limit: LimitInfo = {
			available: contract.availableAmount ? toFloat(contract.availableAmount) : undefined,
			total: contract.totalLimit ? toFloat(contract.totalLimit) : undefined,
			state: contract.availableAmountState ?? undefined,
			annualAvailable: contract.availableAnnualAmount
				? toFloat(contract.availableAnnualAmount)
				: undefined,
			annualTotal: contract.totalAnnualLimit ? toFloat(contract.totalAnnualLimit) : undefined,
			amendmentState: contract.limitAmendmentEntryState ?? undefined,
			increaseOfferAvailable:
				contract.preAmendmentLimitIncreaseOffer?.limitIncreaseAvailable ?? undefined,
		};
		const info = contract.revolvingLoanContractAdditionalInfo;
		const revolving: RevolvingInfo | undefined = info
			? { state: info.state ?? undefined, applicationState: info.applicationState ?? undefined }
			: undefined;
		return revolving ? { limit, revolving } : { limit };
	},
};

interface BillingRow {
	balance?: Billing['balance'];
	totalAmountInvested?: Billing['totalAmountInvested'];
	dueDate?: Billing['dueDate'];
	currentDpd?: Billing['currentDpd'];
	isPaid?: Billing['isPaid'];
	paymentAvailability?: Billing['paymentAvailability'];
	additionalInfoDeferment?: {
		minimumToPay?: Billing['additionalInfoDeferment']['minimumToPay'];
		state?: Billing['additionalInfoDeferment']['state'];
	} | null;
}

export const billingSource: DomainSource<{ myContractBillings?: Paginated<BillingRow> }> = {
	domains: ['billing'],
	query: JarabotBillingQuery,
	toModel: (data) => {
		const billing = data.myContractBillings?.data?.[0];
		if (!billing) return {};
		const deferment = billing.additionalInfoDeferment;
		const info: BillingInfo = {
			balance: billing.balance ? toFloat(billing.balance) : undefined,
			total: billing.totalAmountInvested ? toFloat(billing.totalAmountInvested) : undefined,
			minimumToPay: deferment?.minimumToPay ? toFloat(deferment.minimumToPay) : undefined,
			dueDate: toIsoDate(billing.dueDate),
			daysOverdue: billing.currentDpd ?? undefined,
			isPaid: billing.isPaid ?? undefined,
			canPay: isBillingPayable(billing),
			paymentMethods: billing.paymentAvailability?.methodSlugs ?? undefined,
			canDefer: deferment?.state === BillingDefermentStateEnum.AVAILABLE,
		};
		return { billing: info };
	},
};
